/**
 * Builds the downloadable issue-report payload from the **full branch
 * tree** (assistant-ui's `ExportedMessageRepository`, the same
 * branch-complete structure the history adapter persists), not the
 * flattened active-branch `thread.messages` view-model.
 *
 * Two goals:
 *
 * 1. **Branch completeness, tree-shaped** — every sibling
 *    (edits/regenerations) is included. The flat `parentId` graph is
 *    reorganised into a *branch-point tree*: a linear run of messages
 *    is a flat `TreeNode[]` "line"; nesting (`branches`) appears **only**
 *    where the conversation actually forks. `thread.branches` is the
 *    top-level list of lines — normally one entry, but >1 when the
 *    *first* user message was edited (sibling roots, `parentId: null`).
 *    `thread.headId` still marks the active branch tip so a reader can
 *    locate "current". `parentId` is dropped from nodes (now encoded by
 *    tree position).
 * 2. **Slimming** — `ThreadMessage` is already leaner than the
 *    `MessageState` view-model (no `composer`/`isCopied`/`isHovering`/
 *    `branchNumber`/`isLast`, no `content`+`parts` duplication), but it
 *    still carries redundant/irrelevant fields for a bug report:
 *    `argsText` (redundant with parsed `args`), base64 blobs
 *    (`image`/`file.data`/`audio`), empty `attachments`, empty
 *    `metadata.custom`, and `complete`/`running` statuses. All are
 *    dropped here; only `incomplete`/error statuses survive (they carry
 *    real diagnostic value — they show where a turn failed).
 */
import type {
  ExportedMessageRepository,
  ThreadAssistantMessagePart,
  ThreadMessage,
  ThreadUserMessagePart,
} from '@assistant-ui/react';

/** A message part with its verbose/irrelevant fields stripped. */
export type SlimPart = Record<string, unknown>;

/** A single message in the slimmed transcript (no `parentId` — tree position encodes it). */
export type SlimMessage = {
  id: string;
  role: ThreadMessage['role'];
  createdAt: Date;
  /** Present only for assistant messages whose run did not complete cleanly. */
  status?: unknown;
  /** Present only when non-empty. */
  attachments?: Record<string, unknown>[];
  /** Present only when `metadata.custom` is non-empty. */
  metadata?: Record<string, unknown>;
  parts: SlimPart[];
};

/**
 * A node in the branch-point tree: a slimmed message that, **only when
 * the conversation forks at it**, carries one `branches` line per child
 * sibling. A line is itself a `TreeNode[]`.
 */
export type TreeNode = SlimMessage & { branches?: TreeNode[][] };

export type DiagnosticReport = {
  reportedAt: string;
  description: string;
  thread: {
    id: string;
    headId: string | null;
    /** Top-level lines: one entry normally, >1 if the first message was edited. */
    branches: TreeNode[][];
  };
};

type AnyPart = ThreadUserMessagePart | ThreadAssistantMessagePart;

/** Project a `text`/`reasoning` part: both carry only their textual content. */
function slimTextPart(part: Extract<AnyPart, { type: 'text' | 'reasoning' }>): SlimPart {
  return { type: part.type, text: part.text };
}

/** Project a `source` part, keeping `title` only when present. */
function slimSourcePart(part: Extract<AnyPart, { type: 'source' }>): SlimPart {
  return {
    type: 'source',
    sourceType: part.sourceType,
    id: part.id,
    url: part.url,
    ...(part.title && { title: part.title }),
  };
}

/** Project an `image` part. Base64 blobs are useless in a bug report and dominate payload size. */
function slimImagePart(part: Extract<AnyPart, { type: 'image' }>): SlimPart {
  return { type: 'image', ...(part.filename && { filename: part.filename }), omitted: 'image data' };
}

/** Project a `file` part, dropping the base64 blob. */
function slimFilePart(part: Extract<AnyPart, { type: 'file' }>): SlimPart {
  return {
    type: 'file',
    mimeType: part.mimeType,
    ...(part.filename && { filename: part.filename }),
    omitted: 'file data',
  };
}

/** Project an `audio` part, dropping the base64 blob. */
function slimAudioPart(part: Extract<AnyPart, { type: 'audio' }>): SlimPart {
  return { type: 'audio', format: part.audio.format, omitted: 'audio data' };
}

/** Project a `data` part. */
function slimDataPart(part: Extract<AnyPart, { type: 'data' }>): SlimPart {
  return { type: 'data', name: part.name, data: part.data };
}

/**
 * Project a `tool-call` part. Drop `argsText` (redundant with the parsed `args`),
 * `artifact`, `parentId`, and the nested `messages` echo. Keep `result`/`isError`/
 * `interrupt` only when present.
 */
function slimToolCallPart(part: Extract<AnyPart, { type: 'tool-call' }>): SlimPart {
  return {
    type: 'tool-call',
    toolName: part.toolName,
    toolCallId: part.toolCallId,
    args: part.args,
    ...(part.result !== undefined && { result: part.result }),
    ...(part.isError && { isError: true }),
    ...(part.interrupt && { interrupt: part.interrupt }),
  };
}

/** Project a `generative-ui` part, dropping the verbose UI spec tree. */
function slimGenerativeUiPart(part: Extract<AnyPart, { type: 'generative-ui' }>): SlimPart {
  return { type: 'generative-ui', ...(part.id && { id: part.id }), omitted: 'generative ui spec' };
}
/**
 * Projector lookup keyed by part type. `satisfies` makes exhaustiveness over
 * `AnyPart['type']` compiler-checked: a newly added part type fails to type-check
 * here until it gets an explicit projector.
 */
const SLIM_PART_BY_TYPE = {
  text: slimTextPart,
  reasoning: slimTextPart,
  source: slimSourcePart,
  image: slimImagePart,
  file: slimFilePart,
  audio: slimAudioPart,
  data: slimDataPart,
  'generative-ui': slimGenerativeUiPart,
  'tool-call': slimToolCallPart,
} satisfies { [Type in AnyPart['type']]: (part: Extract<AnyPart, { type: Type }>) => SlimPart };

/**
 * Fallback for a part whose tag is not in the compile-time union (e.g. data
 * read from untyped persistence). Surface only the unknown tag — never pass the
 * raw part through unstripped.
 */
function slimUnknownPart(type: string): SlimPart {
  return { type, omitted: 'unknown part type' };
}

/** Strip a single message part down to its diagnostically meaningful fields. */
function slimPart(part: AnyPart): SlimPart {
  const project = SLIM_PART_BY_TYPE[part.type] as ((part: AnyPart) => SlimPart) | undefined;
  if (project) {
    return project(part);
  }
  return slimUnknownPart((part as { type: string }).type);
}

/** True for statuses that carry diagnostic value (a turn that did not finish cleanly). */
function isNoteworthyStatus(status: { type: string } | undefined): boolean {
  return status?.type === 'incomplete';
}

function slimMessage(message: ThreadMessage): SlimMessage {
  const custom = message.metadata?.custom;
  const status = 'status' in message ? message.status : undefined;
  const attachments = 'attachments' in message ? message.attachments : undefined;

  return {
    id: message.id,
    role: message.role,
    createdAt: message.createdAt,
    ...(isNoteworthyStatus(status) && { status }),
    ...(attachments &&
      attachments.length > 0 && {
        attachments: attachments.map((a) => ({ id: a.id, type: a.type, name: a.name, contentType: a.contentType })),
      }),
    ...(custom && Object.keys(custom).length > 0 && { metadata: custom }),
    parts: message.content.map((part) => slimPart(part as AnyPart)),
  };
}

/**
 * Reorganise the flat `parentId` graph into branch-point tree lines.
 *
 * Walks the single-child chain into one flat `TreeNode[]`; at a message
 * with ≥2 children it attaches `branches` (one recursively-built line
 * per child, in repository order) and ends the line there. Child order
 * follows `repo.messages` order, so the persisted append order — and
 * thus the `headId` active branch — is preserved.
 */
function buildLines(repo: ExportedMessageRepository): TreeNode[][] {
  const messageById = new Map(repo.messages.map(({ message }) => [message.id, message] as const));
  const childIdsByParent = repo.messages.reduce((acc, { message, parentId }) => {
    acc.set(parentId, [...(acc.get(parentId) ?? []), message.id]);
    return acc;
  }, new Map<string | null, string[]>());

  // Recursion depth tracks transcript length — fine for a bounded
  // issue report.
  const buildLine = (startId: string): TreeNode[] => {
    const message = messageById.get(startId);
    if (!message) {
      return [];
    }
    const node: TreeNode = slimMessage(message);
    const childIds: string[] = childIdsByParent.get(startId) ?? [];
    if (childIds.length > 1) {
      return [{ ...node, branches: childIds.map((childId: string) => buildLine(childId)) }];
    }
    const [nextId] = childIds;
    return nextId === undefined ? [node] : [node, ...buildLine(nextId)];
  };

  return (childIdsByParent.get(null) ?? []).map((rootId: string) => buildLine(rootId));
}

/**
 * Project a full `ExportedMessageRepository` (branches intact) into the
 * slimmed, branch-point-tree-shaped downloadable diagnostic report.
 */
export function buildDiagnosticReport(args: {
  repo: ExportedMessageRepository;
  threadId: string;
  description: string;
  reportedAt: string;
}): DiagnosticReport {
  const { repo, threadId, description, reportedAt } = args;
  return {
    reportedAt,
    description,
    thread: {
      id: threadId,
      headId: repo.headId ?? null,
      branches: buildLines(repo),
    },
  };
}
